import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Sparkles,
  Trash2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import {
  Page,
  Main,
  Card,
  Button,
  Badge,
  Spinner,
  LoadingState,
  ErrorState,
  EmptyState,
  Modal,
  Field,
  Thumb,
  inputClass,
} from '@/components/ui';
import { useApi, useMutation } from '@/hooks/useApi';
import api from '@/lib/api';
import { ITEM_CATEGORIES, COLORS, MATERIALS } from '@/lib/constants';
import { formatRelativeDate, cn } from '@/lib/utils';
import type { GapEntry, IdealItem, Item } from '@/lib/types';

type Tab = 'gaps' | 'attention' | 'ideal';

const STATUS_STYLES: Record<GapEntry['status'], string> = {
  missing: 'text-red-600 bg-red-50',
  under: 'text-amber-600 bg-amber-50',
  met: 'text-green-600 bg-green-50',
  over: 'text-blue-600 bg-blue-50',
};

export function GapReportPage() {
  const [tab, setTab] = useState<Tab>('gaps');

  return (
    <Page>
      <PageHeader section="Gap Report" backHref="/before-buy" backLabel="Before You Buy" />
      <Main className="space-y-5">
        <div className="flex bg-secondary rounded-xl p-1">
          {(
            [
              { id: 'gaps', label: 'Gap Analysis' },
              { id: 'attention', label: 'Needs Attention' },
              { id: 'ideal', label: 'Ideal Wardrobe' },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'flex-1 py-2.5 rounded-lg text-sm font-medium transition-all',
                tab === t.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'gaps' && <GapAnalysisTab />}
        {tab === 'attention' && <NeedsAttentionTab />}
        {tab === 'ideal' && <IdealWardrobeTab />}
      </Main>
    </Page>
  );
}

// --- Gap Analysis -----------------------------------------------------------

function GapAnalysisTab() {
  const { data, loading, error } = useApi<{ gaps: GapEntry[] }>('/gap-report');
  const gaps = data?.gaps ?? [];

  const missing = gaps.filter((g) => g.status === 'missing').length;
  const under = gaps.filter((g) => g.status === 'under').length;
  const complete = gaps.filter((g) => g.status === 'met' || g.status === 'over').length;
  const toFill = gaps.filter((g) => g.status === 'missing' || g.status === 'under');

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (gaps.length === 0) {
    return (
      <EmptyState
        icon={<Sparkles className="w-8 h-8" />}
        title="No ideal wardrobe defined"
        description="Add ideal items (or generate them with AI) to see how your wardrobe measures up."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-red-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-red-600">{missing}</p>
          <p className="text-xs text-red-600/80">Missing</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-amber-600">{under}</p>
          <p className="text-xs text-amber-600/80">Under</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-green-600">{complete}</p>
          <p className="text-xs text-green-600/80">Complete</p>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Gaps to Fill</h3>
        {toFill.length === 0 ? (
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Your wardrobe meets every ideal. Nicely done.</p>
          </Card>
        ) : (
          toFill.map((gap) => <GapCard key={gap.idealItem.id} gap={gap} />)
        )}
      </div>
    </div>
  );
}

function GapCard({ gap }: { gap: GapEntry }) {
  const { idealItem } = gap;
  const detail = [idealItem.color, idealItem.material, idealItem.category].filter(Boolean).join(' · ');
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-foreground">{idealItem.itemType}</h4>
          <p className="text-xs text-muted-foreground">{detail}</p>
        </div>
        <span className={cn('px-2 py-1 rounded-lg text-xs font-medium flex-shrink-0', STATUS_STYLES[gap.status])}>
          {gap.status === 'missing' ? 'Missing' : `${gap.currentCount}/${idealItem.quantity}`}
        </span>
      </div>

      {gap.matchingItems.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border space-y-2">
          <p className="text-xs text-muted-foreground">Current items:</p>
          <div className="flex flex-wrap gap-2">
            {gap.matchingItems.map((item) => (
              <Link
                key={item.id}
                to={`/wardrobe/item/${item.id}`}
                className="flex items-center gap-2 p-1.5 pr-2.5 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
              >
                <Thumb url={item.primaryPhotoUrl} alt={item.name} className="w-8 h-8 rounded-md flex-shrink-0" />
                <span className="text-xs text-foreground">{item.name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3">
        <Link to={`/before-buy/ai-suggestions?category=${encodeURIComponent(idealItem.category)}`}>
          <Button fullWidth className="text-xs py-2">
            <Sparkles className="w-3.5 h-3.5" />
            Find Suggestions
          </Button>
        </Link>
      </div>
    </Card>
  );
}

// --- Needs Attention --------------------------------------------------------

function NeedsAttentionTab() {
  const { data, loading, error } = useApi<{ needsReplacement: Item[]; underused: Item[] }>('/gap-report/attention');
  const needsReplacement = data?.needsReplacement ?? [];
  const underused = data?.underused ?? [];

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (needsReplacement.length === 0 && underused.length === 0) {
    return (
      <EmptyState
        icon={<TrendingUp className="w-8 h-8" />}
        title="Nothing needs attention"
        description="No heavily worn or underused items right now."
      />
    );
  }

  return (
    <div className="space-y-6">
      {needsReplacement.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-red-500" />
            <h3 className="text-sm font-semibold text-foreground">Heavy Use — Consider Replacing</h3>
          </div>
          {needsReplacement.map((item) => (
            <AttentionCard key={item.id} item={item} tone="danger" label="Replace" />
          ))}
        </section>
      )}

      {underused.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-semibold text-foreground">Underused — Consider Selling</h3>
          </div>
          {underused.map((item) => (
            <AttentionCard key={item.id} item={item} tone="warning" label="Low Use" />
          ))}
        </section>
      )}
    </div>
  );
}

function AttentionCard({ item, tone, label }: { item: Item; tone: 'danger' | 'warning'; label: string }) {
  return (
    <Link to={`/wardrobe/item/${item.id}`}>
      <Card className="p-4 hover:border-primary/30 transition-all">
        <div className="flex items-start gap-3">
          <Thumb url={item.primaryPhotoUrl} alt={item.name} className="w-12 h-12 rounded-lg flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h4 className="text-sm font-medium text-foreground truncate">{item.name}</h4>
                <p className="text-xs text-muted-foreground">{item.brand ?? item.category}</p>
              </div>
              <Badge tone={tone} className="flex-shrink-0">
                {label}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <Stat label="Wears" value={String(item.wearCount)} />
              <Stat label="Owned" value={item.acquisitionDate ? formatRelativeDate(item.acquisitionDate) : '—'} />
              <Stat label="Last Worn" value={formatRelativeDate(item.lastWornAt)} />
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center p-2 bg-secondary/50 rounded-lg">
      <p className="text-xs font-semibold text-foreground truncate">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

// --- Ideal Wardrobe ---------------------------------------------------------

interface AiTemplateItem {
  category: string;
  itemType: string;
  color: string;
  material: string;
  quantity: number;
}

function IdealWardrobeTab() {
  const { data, loading, error, refetch } = useApi<{ idealItems: IdealItem[] }>('/ideal-items');
  const { mutate } = useMutation();
  const [showAdd, setShowAdd] = useState(false);
  const [showAi, setShowAi] = useState(false);

  const idealItems = data?.idealItems ?? [];
  const knownCategories: string[] = [...ITEM_CATEGORIES];
  const present = knownCategories.filter((c) => idealItems.some((i) => i.category === c));
  const extra = Array.from(
    new Set(idealItems.map((i) => i.category).filter((c) => !knownCategories.includes(c))),
  );
  const orderedCategories = [...present, ...extra];

  const handleDelete = async (id: string) => {
    await mutate('delete', `/ideal-items/${id}`);
    refetch();
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button variant="outline" fullWidth onClick={() => setShowAdd(true)}>
          <Plus className="w-4 h-4" />
          Add Item
        </Button>
        <Button fullWidth onClick={() => setShowAi(true)}>
          <Sparkles className="w-4 h-4" />
          Generate with AI
        </Button>
      </div>

      {loading && <LoadingState />}
      {error && <ErrorState message={error} />}

      {!loading && !error && idealItems.length === 0 && (
        <EmptyState
          icon={<Sparkles className="w-8 h-8" />}
          title="No ideal items yet"
          description="Define your ideal wardrobe manually or generate one with AI."
        />
      )}

      <div className="space-y-3">
        {orderedCategories.map((category) => {
          const items = idealItems.filter((i) => i.category === category);
          if (items.length === 0) return null;
          return (
            <Card key={category} className="overflow-hidden">
              <div className="px-4 py-3 bg-secondary/30 border-b border-border">
                <h4 className="text-sm font-semibold text-foreground">{category}</h4>
              </div>
              <div className="divide-y divide-border">
                {items.map((item) => (
                  <div key={item.id} className="px-4 py-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-foreground truncate">{item.itemType}</p>
                        {item.source === 'ai' && <Badge tone="accent">AI</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {[item.color, item.material].filter(Boolean).join(' · ')}
                        {[item.color, item.material].filter(Boolean).length > 0 ? ' · ' : ''}
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                      aria-label="Delete ideal item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      <AddIdealItemModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onAdded={() => {
          setShowAdd(false);
          refetch();
        }}
      />
      <AiGenerateModal
        isOpen={showAi}
        onClose={() => setShowAi(false)}
        onSaved={() => {
          setShowAi(false);
          refetch();
        }}
      />
    </div>
  );
}

function AddIdealItemModal({
  isOpen,
  onClose,
  onAdded,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { mutate, loading, error } = useMutation();
  const [category, setCategory] = useState('');
  const [itemType, setItemType] = useState('');
  const [color, setColor] = useState('');
  const [material, setMaterial] = useState('');
  const [quantity, setQuantity] = useState(1);

  const canSave = category !== '' && itemType.trim() !== '' && !loading;

  const handleSave = async () => {
    if (!canSave) return;
    const result = await mutate('post', '/ideal-items', {
      category,
      itemType: itemType.trim(),
      color: color || undefined,
      material: material || undefined,
      quantity,
    });
    if (result !== null) {
      setCategory('');
      setItemType('');
      setColor('');
      setMaterial('');
      setQuantity(1);
      onAdded();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Ideal Item"
      footer={
        <Button fullWidth onClick={handleSave} disabled={!canSave}>
          {loading ? 'Adding…' : 'Add to Ideal Wardrobe'}
        </Button>
      }
    >
      <div className="space-y-4">
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Field label="Category">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
            <option value="">Select category</option>
            {ITEM_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Type">
          <input
            type="text"
            value={itemType}
            onChange={(e) => setItemType(e.target.value)}
            placeholder="e.g., Oxford Shoes, White Dress Shirt"
            className={inputClass}
          />
        </Field>
        <Field label="Color">
          <select value={color} onChange={(e) => setColor(e.target.value)} className={inputClass}>
            <option value="">Any color</option>
            {COLORS.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Material">
          <select value={material} onChange={(e) => setMaterial(e.target.value)} className={inputClass}>
            <option value="">Any material</option>
            {MATERIALS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Quantity">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="w-10 h-10 bg-secondary rounded-lg text-foreground hover:bg-muted transition-colors"
            >
              −
            </button>
            <span className="text-lg font-semibold text-foreground w-8 text-center">{quantity}</span>
            <button
              type="button"
              onClick={() => setQuantity((q) => q + 1)}
              className="w-10 h-10 bg-secondary rounded-lg text-foreground hover:bg-muted transition-colors"
            >
              +
            </button>
          </div>
        </Field>
      </div>
    </Modal>
  );
}

function AiGenerateModal({
  isOpen,
  onClose,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [template, setTemplate] = useState<AiTemplateItem[] | null>(null);
  const [philosophy, setPhilosophy] = useState<string>('');

  const reset = () => {
    setGenerating(false);
    setSaving(false);
    setError(null);
    setTemplate(null);
    setPhilosophy('');
  };

  const handleClose = () => {
    if (generating || saving) return;
    reset();
    onClose();
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const result = await api.post<{ template: AiTemplateItem[]; philosophy: string }>('/ai/ideal-wardrobe', {});
      setTemplate(result.template ?? []);
      setPhilosophy(result.philosophy ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!template) return;
    setSaving(true);
    setError(null);
    try {
      await api.post('/ideal-items/bulk', { items: template, replace: true });
      reset();
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Generate Ideal Wardrobe"
      footer={
        template ? (
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button fullWidth onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : `Save ${template.length} items`}
            </Button>
          </div>
        ) : (
          <Button fullWidth onClick={handleGenerate} disabled={generating}>
            {generating ? 'Generating…' : 'Generate'}
          </Button>
        )
      }
    >
      {error && <p className="text-sm text-destructive mb-3">{error}</p>}

      {generating && (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Spinner className="w-8 h-8" />
          <p className="text-sm text-muted-foreground text-center">
            AI is analyzing your style and building a recommended wardrobe…
          </p>
        </div>
      )}

      {!generating && !template && (
        <p className="text-sm text-muted-foreground">
          AI will create a personalized ideal wardrobe based on your current items, fit history, and style. Saving
          will replace your existing ideal wardrobe.
        </p>
      )}

      {!generating && template && (
        <div className="space-y-3">
          {philosophy && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-3">
              <p className="text-xs text-muted-foreground leading-relaxed">{philosophy}</p>
            </div>
          )}
          <p className="text-xs font-medium text-muted-foreground">{template.length} proposed items</p>
          <div className="space-y-2">
            {template.map((item, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 bg-secondary/40 rounded-lg">
                <div className="min-w-0">
                  <p className="text-sm text-foreground truncate">{item.itemType}</p>
                  <p className="text-xs text-muted-foreground">
                    {[item.color, item.material, item.category].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground flex-shrink-0">×{item.quantity}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-amber-700 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            Saving replaces your current ideal wardrobe.
          </p>
        </div>
      )}
    </Modal>
  );
}
